import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import Table from "../../../components/table/MuiTable";
import { dateChanger } from "../../../shared/extra";
import VoucherView from "../../../components/alertPopup/VoucherView";
import { columns as originalColumns } from "./columns";
import TableFilter from "../../../components/tableFilter";
import {
  fetchUserOrder,
  fetchVoucherDetails,
} from "../../../apiCalls/urlFunction";
import CursorPagination from "../../../components/pagination/CursorPagination";

const makeCustomRows = (arr) => {
  return arr?.map((item) => {
    // Check if item already has customOrderDenominations (from sentizedOrderData)
    if (item.customOrderDenominations) {
      const denom = item.customOrderDenominations;
      return {
        id: item.id || item?.node?.id,
        Id: item?.node?.referenceId || item.referenceId,
        date: dateChanger(item?.node?.createdAt || item.createdAt),
        createdAt: item?.node?.createdAt || item.createdAt,
        product: item?.node?.productDatum?.name || item.product || "N/A",
        amount: Number(denom?.grossItemAmount || 0).toFixed(2),
        youpay: Number(
          denom?.totalNetAmount ||
          (denom?.netItemAmount * denom?.quantity) || 0
        ).toFixed(2),
        quantity: denom?.quantity || 0,
        discount: denom?.cashbackOrDiscount === "DISCOUNT"
          ? `${Number(denom?.discountValue || 0).toFixed(2)} %`
          : "-",
        cashback: denom?.cashbackOrDiscount === "CASHBACK"
          ? `${Number(denom?.discountValue || 0).toFixed(2)} %`
          : "-",
        orderStatus: item?.orderStatus || item?.node?.status,
        showButton: (item?.orderStatus || item?.node?.status) === "COMPLETED",
        order: item,
        isMultiDenom: item.isMultiDenom || false,
        denominationId: item.denominationId,
        customOrderDenominations: denom,
        // Store original order ID for reference
        originalOrderId: item?.order?.node?.id || item?.node?.id || item?.id,
      };
    }

    // Original logic for non-sanitized data
    const denominations = item?.node?.orderDenominations || [];

    return {
      id: item?.node?.id,
      Id: item?.node?.referenceId,
      date: dateChanger(item?.node?.createdAt),
      createdAt: item?.node?.createdAt,
      product: item?.node?.productDatum?.name || "N/A",
      amount: item?.node?.grossAmount?.toFixed(2),
      youpay: item?.node?.netAmount?.toFixed(2),

      // Total Quantity
      quantity: denominations.reduce(
        (total, denomination) => total + (denomination.quantity || 0),
        0,
      ),

      // Total Discount
      discount: denominations.some((d) => d.cashbackOrDiscount === "DISCOUNT")
        ? `${denominations
            .filter((d) => d.cashbackOrDiscount === "DISCOUNT")
            .reduce(
              (total, denomination) =>
                total + (denomination.discountValue || 0),
              0,
            )
            .toFixed(2)} %`
        : "-",

      // Total Cashback
      cashback: denominations.some((d) => d.cashbackOrDiscount === "CASHBACK")
        ? `${denominations
            .filter((d) => d.cashbackOrDiscount === "CASHBACK")
            .reduce(
              (total, denomination) =>
                total + (denomination.discountValue || 0),
              0,
            )
            .toFixed(2)} %`
        : "-",

      orderStatus: item?.node?.status,
      showButton: item?.node?.status === "COMPLETED",
      order: item,
      isMultiDenom: false,
      originalOrderId: item?.node?.id,
    };
  });
};

// FIXED: Properly sanitize order data to create separate rows for each denomination
const sentizedOrderData = (data) => {
  if (!data?.length) return [];

  let finalData = [];

  data?.forEach((item) => {
    const denominations = item?.order?.node?.orderDenominations || [];
    const originalId = item?.id || item?.node?.id;

    if (denominations.length > 1) {
      // Multiple denominations - create a row for each
      denominations.forEach((denomination, index) => {
        const newItem = {
          ...item,
          // Use a consistent ID format that includes the original ID
          id: `${originalId}-${denomination.id || index}`,
          // Override ALL denomination-specific fields
          amount: Number(denomination?.grossItemAmount || 0).toFixed(2),
          youpay: Number(
            denomination?.totalNetAmount ||
            (denomination?.netItemAmount * denomination?.quantity) || 0
          ).toFixed(2),
          quantity: denomination?.quantity || 0,
          discount: denomination?.cashbackOrDiscount === "DISCOUNT"
            ? `${Number(denomination?.discountValue || 0).toFixed(2)} %`
            : "-",
          cashback: denomination?.cashbackOrDiscount === "CASHBACK"
            ? `${Number(denomination?.discountValue || 0).toFixed(2)} %`
            : "-",
          // Override product if it's denomination-specific
          product: item?.product || item?.order?.node?.productDatum?.name || "N/A",
          // Store the specific denomination data
          customOrderDenominations: denomination,
          denominationIndex: index,
          isMultiDenom: true,
          denominationId: denomination?.id,
          // Make sure orderStatus is properly set
          orderStatus: item?.orderStatus || item?.order?.node?.status,
          showButton: (item?.orderStatus || item?.order?.node?.status) === "COMPLETED",
          // Override order object to only contain this denomination
          order: {
            ...item.order,
            node: {
              ...item.order?.node,
              orderDenominations: [denomination], // Only this denomination
            }
          },
          // Store original order ID for reference
          originalOrderId: originalId,
        };
        finalData.push(newItem);
      });
    } else {
      // Single denomination - keep as is but add customOrderDenominations
      const singleDenom = denominations[0] || {};
      const newItem = {
        ...item,
        customOrderDenominations: singleDenom,
        denominationIndex: 0,
        isMultiDenom: false,
        denominationId: singleDenom?.id,
        // Ensure orderStatus is properly set
        orderStatus: item?.orderStatus || item?.order?.node?.status,
        showButton: (item?.orderStatus || item?.order?.node?.status) === "COMPLETED",
        originalOrderId: originalId,
      };
      finalData.push(newItem);
    }
  });

  return finalData;
};

const Order = () => {
  const [activeRowId, setActiveRowId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vouchers, setVouchers] = useState({
    amount: [],
    ProductData: {},
    vouchers: [],
    showVoucher: false,
    loading: false,
  });
  const [ordersData, setOrdersData] = useState({
    orders: [],
    loading: true,
  });

  // VOUCHER - Updated to handle denomination-specific data
  const fetchVoucherDetail = React.useCallback(async (params) => {
    setVouchers((prev) => ({
      ...prev,
      loading: true,
    }));

    const rowData = params?.row || {};
    // Use originalOrderId if available, otherwise use the row id
    const orderId = rowData?.originalOrderId || rowData?.order?.node?.id || rowData?.id;
    const denominationId = rowData?.denominationId;

    try {
      const voucherResponse = await fetchVoucherDetails(orderId);

      if (voucherResponse?.status) {
        const allDenominations = voucherResponse?.data?.orderDenominations || [];

        // If we have a specific denomination ID, filter for that one
        let filteredDenominations = allDenominations;
        let selectedDenomination = null;

        if (denominationId && allDenominations.length > 1) {
          // Find the specific denomination
          selectedDenomination = allDenominations.find(
            d => d.id === denominationId || d.denominationId === denominationId
          );

          if (selectedDenomination) {
            filteredDenominations = [selectedDenomination];
          }
        }

        setVouchers({
          ProductData: voucherResponse?.data?.productDatum || {},
          amount: filteredDenominations,
          vouchers: voucherResponse?.data?.orderVouchers || [],
          orderRowData: rowData?.order?.node || rowData?.order,
          showVoucher: true,
          loading: false,
          selectedDenomination: selectedDenomination || null,
          isMultiDenomView: allDenominations.length > 1 && !!denominationId,
        });
      } else {
        setVouchers((prev) => ({
          ...prev,
          loading: false,
          showVoucher: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching voucher details:", error);
      setVouchers((prev) => ({
        ...prev,
        loading: false,
        showVoucher: false,
      }));
    }
  }, []);

  // COLUMNS
  const columns = React.useMemo(() => {
    return originalColumns
      .map((col) => {
        if (col.field === "amount") {
          return {
            ...col,
            renderCell: (params) => {
              return params.row.amount;
            },
          };
        }
        if (col.field === "product") {
          return {
            ...col,
            renderCell: (params) => {
              return params?.row?.product;
            },
          };
        }
        if (col.field === "quantity") {
          return {
            ...col,
            renderCell: (params) => {
              // If it's a multi-denom row, show the specific quantity
              if (params.row.isMultiDenom) {
                return params.row.quantity;
              }

              // For single denom or aggregated view
              const denominations =
                params.row.order?.node?.orderDenominations || [];
              if (denominations.length <= 1) {
                return params.row.quantity;
              }
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    py: 1,
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  {denominations.map((denom, idx) => (
                    <Typography
                      key={idx}
                      sx={{ fontSize: "13px", fontFamily: "Be Vietnam Pro" }}
                    >
                      {denom?.quantity}
                    </Typography>
                  ))}
                </Box>
              );
            },
          };
        }
        if (col.field === "discount") {
          return {
            ...col,
            renderCell: (params) => {
              // If it's a multi-denom row, show the specific discount
              if (params.row.isMultiDenom) {
                return params.row.discount;
              }

              const denominations =
                params.row.order?.node?.orderDenominations || [];
              if (denominations.length <= 1) {
                const firstDenom = denominations[0];
                if (
                  firstDenom &&
                  firstDenom.cashbackOrDiscount === "DISCOUNT"
                ) {
                  return `${Number(firstDenom.discountValue || 0).toFixed(2)} %`;
                }
                return "-";
              }
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    py: 1,
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  {denominations.map((denom, idx) => (
                    <Typography
                      key={idx}
                      sx={{ fontSize: "13px", fontFamily: "Be Vietnam Pro" }}
                    >
                      {denom.cashbackOrDiscount === "DISCOUNT"
                        ? `${Number(denom.discountValue || 0).toFixed(2)} %`
                        : "-"}
                    </Typography>
                  ))}
                </Box>
              );
            },
          };
        }
        if (col.field === "cashback") {
          return {
            ...col,
            renderCell: (params) => {
              // If it's a multi-denom row, show the specific cashback
              if (params.row.isMultiDenom) {
                return params.row.cashback;
              }

              const denominations =
                params.row.order?.node?.orderDenominations || [];
              if (denominations.length <= 1) {
                const firstDenom = denominations[0];
                if (
                  firstDenom &&
                  firstDenom.cashbackOrDiscount === "CASHBACK"
                ) {
                  return `${Number(firstDenom.discountValue || 0).toFixed(2)} %`;
                }
                return "-";
              }
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    py: 1,
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  {denominations.map((denom, idx) => (
                    <Typography
                      key={idx}
                      sx={{ fontSize: "13px", fontFamily: "Be Vietnam Pro" }}
                    >
                      {denom.cashbackOrDiscount === "CASHBACK"
                        ? `${Number(denom.discountValue || 0).toFixed(2)} %`
                        : "-"}
                    </Typography>
                  ))}
                </Box>
              );
            },
          };
        }
        if (col.field === "youpay") {
          return {
            ...col,
            renderCell: (params) => {
              // If it's a multi-denom row, show the specific youpay
              if (params.row.isMultiDenom) {
                return params.row.youpay;
              }

              const denominations =
                params.row.order?.node?.orderDenominations || [];
              if (denominations.length <= 1) {
                return params.row.youpay;
              }
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    py: 1,
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  {denominations.map((denom, idx) => (
                    <Typography
                      key={idx}
                      sx={{ fontSize: "13px", fontFamily: "Be Vietnam Pro" }}
                    >
                      {Number(
                        denom.totalNetAmount ||
                          denom.netItemAmount * denom.quantity,
                      ).toFixed(2)}
                    </Typography>
                  ))}
                </Box>
              );
            },
          };
        }
        return col;
      })
      .concat([
        {
          field: "action",
          headerName: "Action",
          flex: 1,
          minWidth: 100,
          headerAlign: "center",
          align: "center",
          renderCell: (params) =>
            params.row.showButton ? (
              <Button
                variant="outlined"
                sx={{
                  fontWeight: 400,
                  textTransform: "capitalize",
                  minWidth: 60,
                  fontSize: "12.5px",
                  padding: "2px 10px",
                  fontFamily: "Be Vietnam Pro",
                  borderRadius: 10,
                  background: "#002255",
                  color: "#FFF",
                  "&:hover": {
                    background: "#003366",
                  },
                }}
                onClick={() => {
                  // Use the actual row ID from the params
                  const rowId = params.row?.id || params.id;
                  setActiveRowId(rowId);
                  fetchVoucherDetail(params);
                }}
              >
                View
              </Button>
            ) : (
              "-"
            ),
        },
      ]);
  }, [originalColumns, fetchVoucherDetail]);

  // FILTERED DATA
  const filteredOrders = React.useMemo(() => {
    return ordersData?.orders?.filter((order) => {
      const statusMatch =
        filterStatus === "All"
          ? true
          : order?.orderStatus?.toLowerCase() === filterStatus?.toLowerCase();

      const orderDate = new Date(order?.date);

      const startMatch = startDate
        ? orderDate >= new Date(startDate + "T00:00:00")
        : true;

      const endMatch = endDate
        ? orderDate <= new Date(endDate + "T23:59:59")
        : true;

      return statusMatch && startMatch && endMatch;
    });
  }, [ordersData.orders, filterStatus, startDate, endDate]);

  // DATE FILTER
  const handleDateChange = (type, value) => {
    if (type === "start") setStartDate(value);
    else setEndDate(value);
  };

  // CLEAR FILTERS
  const clearFilters = () => {
    setFilterStatus("All");
    setStartDate("");
    setEndDate("");
  };

  const isFiltered = filterStatus !== "All" || !!startDate || !!endDate;
  const finalSenitizeData = sentizedOrderData(filteredOrders);

  return (
    <Box
      sx={{
        maxWidth: {
          xs: "100%",
          lg: "calc(100% - 30px)",
          xl: "100%",
        },
        overflowX: "auto",
      }}
    >
      <TableFilter
        options={["All", "Completed", "Failed", "Processing"]}
        selectedValue={filterStatus}
        onChange={(val) => setFilterStatus(val)}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        onClearFilters={clearFilters}
      />

      {ordersData?.loading || vouchers?.loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2, fontSize: "17px" }}>
            Loading...
          </Typography>
        </Box>
      ) : (
        <Table
          rows={finalSenitizeData}
          columns={columns}
          errorMessage="Order"
          activeRowId={activeRowId}
          getRowHeight={() => "auto"}
        />
      )}

      <CursorPagination
        fetchFunction={fetchUserOrder}
        limit={isFiltered ? 100 : 10}
        dependency={[filterStatus, startDate, endDate]}
        onLoadingChange={(loading) =>
          setOrdersData((prev) => ({
            ...prev,
            loading,
          }))
        }
        onDataChange={(data) =>
          setOrdersData((prev) => ({
            ...prev,
            orders: makeCustomRows(data),
          }))
        }
        responseMapper={(response) => ({
          data: response?.data?.edges || [],
          totalCount: response?.data?.totalCount || 0,
          endCursor: response?.data?.pageInfo?.endCursor || null,
        })}
      />

      <VoucherView
        voucherData={vouchers}
        onClose={() =>
          setVouchers({
            ...vouchers,
            showVoucher: false,
          })
        }
      />
    </Box>
  );
};

export default React.memo(Order);